import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { FolderKanban, Plus, Check, Loader2, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

// Module-level cache so dozens of pickers on one page share one fetch
let _cache = null;
let _cacheAt = 0;
let _inflight = null;
const listeners = new Set();
export const invalidateProjectsCache = () => { _cache = null; listeners.forEach(fn => fn()); };

const fetchProjects = async (force = false) => {
  if (!force && _cache && Date.now() - _cacheAt < 60000) return _cache;
  // Single in-flight request shared by every picker mounted in the same
  // frame (200 bank-feed rows used to fire 200 identical requests).
  if (_inflight) return _inflight;
  _inflight = axios.get(`${API}/projects`, { params: { status: 'active' } })
    .then(({ data }) => {
      _cache = data.projects || [];
      _cacheAt = Date.now();
      return _cache;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
};

/**
 * Universal project picker. Optional everywhere - defaults to "No project".
 * Props: value (project_id|null), onChange(id|null, project|null), createdFrom,
 *        compact (smaller trigger), autoMatched (shows auto chip), disabled, className
 */
export const ProjectSelect = ({ value, onChange, createdFrom = 'document', compact = false, autoMatched = false, disabled = false, className = '' }) => {
  const [projects, setProjects] = useState(_cache || []);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const searchRef = React.useRef(null);

  const load = useCallback((force) => fetchProjects(force).then(setProjects).catch(() => {}), []);

  useEffect(() => {
    load();
    const fn = () => load(true);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, [load]);

  const selected = projects.find(p => p.id === value);
  const filtered = projects.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
  const exactExists = projects.some(p => p.name.toLowerCase() === search.trim().toLowerCase());

  const createProject = async () => {
    const name = search.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/projects`, { name, created_from: createdFrom });
      invalidateProjectsCache();
      await load(true);
      onChange?.(data.project.id, data.project);
      setOpen(false);
      setSearch('');
      if (!data.existed) toast.success(`Project "${data.project.name}" created: available everywhere now`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create project');
    } finally { setCreating(false); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`justify-start gap-1.5 font-normal ${compact ? 'h-8 text-xs px-2' : 'h-9 text-sm'} ${selected ? 'text-slate-800 border-[#108981]/40' : 'text-slate-400'} ${className}`}
          data-testid="project-select-trigger">
          <FolderKanban className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} ${selected ? 'text-[#108981]' : 'text-slate-300'} flex-shrink-0`} />
          <span className="truncate max-w-[140px]">{selected ? selected.name : 'No project'}</span>
          {autoMatched && selected && <Sparkles className="w-3 h-3 text-amber-500 flex-shrink-0" title="Auto-matched from description" />}
          {selected && !disabled && (
            <X className="w-3 h-3 text-slate-400 hover:text-red-500 ml-auto flex-shrink-0"
              onClick={(e) => { e.stopPropagation(); onChange?.(null, null); }}
              data-testid="project-select-clear" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start" data-testid="project-select-popover">
        <div className="p-2 border-b">
          <Input
            autoFocus
            ref={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search or type a new project…"
            className="h-8 text-sm"
            data-testid="project-select-search"
            onKeyDown={(e) => { if (e.key === 'Enter' && !exactExists && search.trim()) { e.preventDefault(); createProject(); } }}
          />
        </div>
        <div className="max-h-52 overflow-y-auto py-1">
          <button
            type="button"
            className="w-full text-left px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-50 flex items-center gap-2"
            onClick={() => { onChange?.(null, null); setOpen(false); }}
            data-testid="project-option-none">
            {!value && <Check className="w-3.5 h-3.5 text-[#108981]" />}<span className={value ? 'ml-5' : ''}>No project</span>
          </button>
          {filtered.map(p => (
            <button
              key={p.id}
              type="button"
              className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
              onClick={() => { onChange?.(p.id, p); setOpen(false); }}
              data-testid={`project-option-${p.id}`}>
              {value === p.id ? <Check className="w-3.5 h-3.5 text-[#108981] flex-shrink-0" /> : <span className="w-3.5" />}
              <span className="truncate">{p.name}</span>
            </button>
          ))}
          {filtered.length === 0 && !search.trim() && (
            <p className="px-3 py-2 text-xs text-slate-400">No projects yet: type a name above to create your first.</p>
          )}
        </div>
        {search.trim() && !exactExists ? (
          <button
            type="button"
            className="w-full text-left px-3 py-2 text-sm text-[#108981] hover:bg-[#108981]/5 border-t flex items-center gap-2 font-medium"
            onClick={createProject}
            disabled={creating}
            data-testid="project-select-create">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create "{search.trim()}"
          </button>
        ) : (
          <button
            type="button"
            className="w-full text-left px-3 py-2 text-sm text-[#108981] hover:bg-[#108981]/5 border-t flex items-center gap-2 font-medium"
            onClick={() => {
              searchRef.current?.focus();
              toast.info('Type the new project name above, then press Enter');
            }}
            data-testid="project-select-new">
            <Plus className="w-3.5 h-3.5" />
            New project
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};
